import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ShipperDispatchTemplate {
  id: string;
  shipper_id: string;
  name: string;
  description: string | null;
  template_data: any;
  use_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

const KEY = (shipperId: string | undefined) => ["shipper-dispatch-templates", shipperId];

export function useShipperDispatchTemplates(shipperId: string | undefined) {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: KEY(shipperId),
    enabled: !!shipperId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shipper_dispatch_templates" as any)
        .select("*")
        .order("last_used_at", { ascending: false, nullsFirst: false })
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ShipperDispatchTemplate[];
    },
  });

  const save = useMutation({
    mutationFn: async (input: { name: string; description?: string | null; template_data: any }) => {
      if (!shipperId) throw new Error("Not signed in");
      const name = input.name.trim();
      const description = input.description?.trim() || null;

      // Case-insensitive lookup of existing template with same name
      const { data: existing, error: lookupErr } = await supabase
        .from("shipper_dispatch_templates" as any)
        .select("id")
        .eq("shipper_id", shipperId)
        .ilike("name", name)
        .maybeSingle();
      if (lookupErr) throw lookupErr;

      if ((existing as any)?.id) {
        const { data, error } = await supabase
          .from("shipper_dispatch_templates" as any)
          .update({ name, description, template_data: input.template_data })
          .eq("id", (existing as any).id)
          .select("*")
          .single();
        if (error) throw error;
        return data as unknown as ShipperDispatchTemplate;
      }

      const { data, error } = await supabase
        .from("shipper_dispatch_templates" as any)
        .insert({ shipper_id: shipperId, name, description, template_data: input.template_data })
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as ShipperDispatchTemplate;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(shipperId) }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("shipper_dispatch_templates" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(shipperId) }),
  });

  const apply = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("apply_shipper_dispatch_template" as any, { _template_id: id });
      if (error) throw error;
      return data as unknown as ShipperDispatchTemplate;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(shipperId) }),
  });

  return {
    templates: list.data ?? [],
    loading: list.isLoading,
    save,
    remove,
    apply,
  };
}
